import {useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";

const FOCUSABLE_SELECTOR = [
    '[data-tv-focusable="true"]:not([disabled]):not([aria-disabled="true"])',
    "button:not(:disabled):not([tabindex='-1'])",
    "a[href]:not([tabindex='-1'])",
    "input:not(:disabled):not([type='hidden']):not([tabindex='-1'])",
    "select:not(:disabled):not([tabindex='-1'])",
    "textarea:not(:disabled):not([tabindex='-1'])",
    "[tabindex='0']:not(:disabled)"
].join(", ");

const MODAL_CONTAINER_SELECTOR = [
    ".tv-modal-overlay",
    ".tv-mode-prompt-backdrop",
    ".custom-modal-v2-content",
    ".scroll-modal:not(.scroll-modal__modal-closed)",
    ".modal.show"
].join(", ");

const GRID_ROW_SELECTORS = [
    "[data-tv-grid='true']",
    ".tv-modal-body",
    ".tv-modal-list",
    ".tv-episodes-grid",
    ".tv-streams-grid",
    ".tv-live-channels-grid",
    ".tv-search-screen__keyboard",
    ".tv-search-screen__grid",
    ".tmdb-item-suggestions__container",
    ".tv-details-screen__tab-panel"
].join(", ");

function isElementVisible(el: HTMLElement | null): boolean {
    if (!el || !el.isConnected) return false;
    const style = window.getComputedStyle(el);
    if (
        style.display === "none" ||
        style.visibility === "hidden" ||
        parseFloat(style.opacity) < 0.05 ||
        style.pointerEvents === "none"
    ) {
        return false;
    }
    const rect = el.getBoundingClientRect();
    return rect.width > 4 && rect.height > 4;
}

function getActiveScopeRoot(): ParentNode {
    const modals = Array.from(document.querySelectorAll<HTMLElement>(MODAL_CONTAINER_SELECTOR)).filter(
        el => isElementVisible(el) && getFocusableInContainer(el).length > 0
    );
    if (modals.length > 0) {
        return modals[modals.length - 1];
    }
    return document;
}

function getMainScrollContainer(el?: HTMLElement | null): HTMLElement | null {
    if (el) {
        const closestScrollable = el.closest<HTMLElement>(
            ".tv-modal-body, .tv-search-screen__right, .squadflix-tv-main, #home__scrollContainer"
        );
        if (closestScrollable) return closestScrollable;
    }
    return document.querySelector<HTMLElement>(".squadflix-tv-main, #home__scrollContainer");
}

/**
 * Returns the true vertical layout position of an element in scroll layout space,
 * so rows inside a scrolling page preserve their stable top-to-bottom order
 * regardless of scroll position.
 */
function getLayoutY(el: HTMLElement, mainScroll?: HTMLElement | null): number {
    if (el.dataset.tvRow === "tv-top-navbar") {
        return -999999;
    }
    const rect = el.getBoundingClientRect();
    const scrollParent = getMainScrollContainer(el) || mainScroll;
    if (scrollParent && scrollParent.contains(el) && scrollParent !== el) {
        const mainRect = scrollParent.getBoundingClientRect();
        return rect.top - mainRect.top + scrollParent.scrollTop;
    }
    return rect.top;
}

function getFocusableInContainer(container: ParentNode): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(isElementVisible);
}

function getPrimaryFocusableInRow(rowEl: HTMLElement): HTMLElement[] {
    const all = getFocusableInContainer(rowEl);
    const primary = all.filter(el => el.dataset.tvCardSecondary !== "true");
    return primary.length > 0 ? primary : all;
}

function isGridContainer(rowEl: HTMLElement): boolean {
    return rowEl.matches(GRID_ROW_SELECTORS) || !!rowEl.querySelector(GRID_ROW_SELECTORS);
}

/**
 * Groups elements into visual horizontal lines (sub-rows) sorted top-to-bottom,
 * with each line sorted left-to-right.
 */
function groupIntoVisualSubRows(elements: HTMLElement[], mainScroll: HTMLElement | null): HTMLElement[][] {
    if (!elements.length) return [];

    const items = elements.map(el => {
        const rect = el.getBoundingClientRect();
        return {
            el,
            x: rect.left,
            y: getLayoutY(el, mainScroll)
        };
    });

    items.sort((a, b) => a.y - b.y || a.x - b.x);

    const rows: {y: number; items: {el: HTMLElement; x: number; y: number}[]}[] = [];
    const ROW_TOLERANCE_PX = 36;

    for (const item of items) {
        const lastRow = rows[rows.length - 1];
        if (!lastRow || Math.abs(item.y - lastRow.y) > ROW_TOLERANCE_PX) {
            rows.push({y: item.y, items: [item]});
        } else {
            lastRow.items.push(item);
        }
    }

    return rows.map(r => {
        r.items.sort((a, b) => a.x - b.x);
        return r.items.map(i => i.el);
    });
}

/**
 * Scrolls horizontal tracks (.tv-media-row__track) and the vertical main viewport
 * independently so Chromium never cancels one axis animation with the other.
 */
function scrollElementIntoTvView(el: HTMLElement) {
    if (!el) return;

    // 1. Horizontal scroll inside a carousel track or pill bar
    const hTrack = el.closest<HTMLElement>(".tv-media-row__track, .tv-season-pills");
    if (hTrack) {
        const cardWrapper = el.closest<HTMLElement>(".tv-media-card-wrapper") || el;
        const trackRect = hTrack.getBoundingClientRect();
        const itemRect = cardWrapper.getBoundingClientRect();
        const itemLeftInTrack = itemRect.left - trackRect.left + hTrack.scrollLeft;

        // If first item in track, snap cleanly to 0
        const primaryItems = getPrimaryFocusableInRow(hTrack);
        if (primaryItems[0] === el || primaryItems[0]?.closest(".tv-media-card-wrapper") === cardWrapper) {
            hTrack.scrollTo({left: 0, behavior: "smooth"});
        } else {
            const targetLeft = Math.max(0, itemLeftInTrack - Math.min(80, trackRect.width * 0.08));
            hTrack.scrollTo({left: targetLeft, behavior: "smooth"});
        }
    }

    // 2. Vertical scroll inside the nearest vertical TV scroll container
    const mainScroll = getMainScrollContainer(el);
    if (!mainScroll) return;

    const rowAttr = el.closest<HTMLElement>("[data-tv-row]")?.dataset.tvRow;
    if (rowAttr === "tv-top-navbar" || rowAttr === "tv-hero") {
        mainScroll.scrollTo({top: 0, behavior: "smooth"});
        return;
    }

    if (mainScroll.contains(el)) {
        const mediaRowSection = el.closest<HTMLElement>(".tv-media-row") || el;
        const mainRect = mainScroll.getBoundingClientRect();
        const targetRect = mediaRowSection.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();

        const sectionTopInMain = targetRect.top - mainRect.top + mainScroll.scrollTop;
        const elBottomInMain = elRect.bottom - mainRect.top + mainScroll.scrollTop;
        const elTopInMain = elRect.top - mainRect.top + mainScroll.scrollTop;

        const viewTop = mainScroll.scrollTop;
        const viewBottom = viewTop + mainRect.height;

        if (mediaRowSection.classList.contains("tv-media-row")) {
            const idealTop = Math.max(0, sectionTopInMain - 48);
            if (sectionTopInMain < viewTop + 24 || elBottomInMain > viewBottom - 40) {
                mainScroll.scrollTo({top: idealTop, behavior: "smooth"});
            }
        } else {
            if (elTopInMain < viewTop + 40) {
                mainScroll.scrollTo({top: Math.max(0, elTopInMain - 60), behavior: "smooth"});
            } else if (elBottomInMain > viewBottom - 40) {
                mainScroll.scrollTo({
                    top: Math.max(0, elBottomInMain - mainRect.height + 80),
                    behavior: "smooth"
                });
            }
        }
    }
}

export const useTvSpatialNavigation = (enabled = true, onBackOverride?: () => boolean) => {
    const navigate = useNavigate();
    const location = useLocation();
    const lastRowItemMap = useRef<Map<string, HTMLElement>>(new Map());

    // Focus initial element on route change
    useEffect(() => {
        if (!enabled) return;
        const timer = setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            if (
                active &&
                active !== document.body &&
                isElementVisible(active) &&
                active.closest(".squadflix-tv-main")
            ) {
                return;
            }
            const scope = getActiveScopeRoot();
            const autoFocusEl = scope.querySelector<HTMLElement>('[data-tv-autofocus="true"]');
            if (autoFocusEl && isElementVisible(autoFocusEl)) {
                autoFocusEl.focus({preventScroll: true});
                scrollElementIntoTvView(autoFocusEl);
                return;
            }
            const mainCandidates = getFocusableInContainer(
                document.querySelector(".squadflix-tv-main") || scope
            );
            if (mainCandidates.length > 0) {
                mainCandidates[0].focus({preventScroll: true});
                scrollElementIntoTvView(mainCandidates[0]);
            }
        }, 120);

        return () => clearTimeout(timer);
    }, [enabled, location.pathname]);

    useEffect(() => {
        if (!enabled) return;

        const focusTarget = (target: HTMLElement, rowEl?: HTMLElement | null) => {
            target.focus({preventScroll: true});
            scrollElementIntoTvView(target);

            const r = rowEl ?? target.closest<HTMLElement>("[data-tv-row]");
            const rowId = r?.dataset.tvRow;
            if (rowId && target.dataset.tvCardSecondary !== "true") {
                lastRowItemMap.current.set(rowId, target);
            }
        };

        const getVisibleRows = (scope: ParentNode, mainScroll: HTMLElement | null) => {
            const rawRows = Array.from(scope.querySelectorAll<HTMLElement>("[data-tv-row]"));
            const validRows: {
                rowEl: HTMLElement;
                rowId: string;
                y: number;
                left: number;
                right: number;
                items: HTMLElement[];
            }[] = [];

            for (const rowEl of rawRows) {
                const items = getFocusableInContainer(rowEl);
                if (!items.length) continue;
                const rect = rowEl.getBoundingClientRect();
                validRows.push({
                    rowEl,
                    rowId: rowEl.dataset.tvRow || "",
                    y: getLayoutY(rowEl, mainScroll),
                    left: rect.left,
                    right: rect.right,
                    items
                });
            }

            validRows.sort((a, b) => a.y - b.y || a.left - b.left);
            return validRows;
        };

        const moveFocus = (direction: "up" | "down" | "left" | "right") => {
            const scope = getActiveScopeRoot();
            const allElements = getFocusableInContainer(scope);
            if (!allElements.length) return;

            const mainScroll = getMainScrollContainer();
            const current = document.activeElement as HTMLElement | null;

            if (!current || !allElements.includes(current)) {
                const autoEl = allElements.find(el => el.dataset.tvAutofocus === "true") || allElements[0];
                focusTarget(autoEl);
                return;
            }

            const currentRowEl = current.closest<HTMLElement>("[data-tv-row]");
            const currentRowId = currentRowEl?.dataset.tvRow;
            if (currentRowId && current.dataset.tvCardSecondary !== "true") {
                lastRowItemMap.current.set(currentRowId, current);
            }

            // 1. Handle card primary <-> card secondary button transitions first
            const cardWrapper = current.closest<HTMLElement>(".tv-media-card-wrapper");
            if (cardWrapper) {
                const primaryBtn = cardWrapper.querySelector<HTMLElement>('[data-tv-card-primary="true"]');
                const secondaryBtn = cardWrapper.querySelector<HTMLElement>('[data-tv-card-secondary="true"]');

                if (direction === "down" && current === primaryBtn && isElementVisible(secondaryBtn)) {
                    focusTarget(secondaryBtn!, currentRowEl);
                    return;
                }
                if (direction === "up" && current === secondaryBtn && isElementVisible(primaryBtn)) {
                    focusTarget(primaryBtn!, currentRowEl);
                    return;
                }
            }

            // 2. If inside a [data-tv-row], handle structured navigation
            if (currentRowEl) {
                const isGrid = isGridContainer(currentRowEl);
                const rowPrimaryItems = getPrimaryFocusableInRow(currentRowEl);

                // Determine which primary item corresponds to `current` (in case `current` is a secondary button)
                const effectiveCurrent =
                    current.dataset.tvCardSecondary === "true" && cardWrapper
                        ? cardWrapper.querySelector<HTMLElement>('[data-tv-card-primary="true"]') || current
                        : current;

                const findSideBySideRow = (dir: "left" | "right") => {
                    const rows = getVisibleRows(scope, mainScroll);
                    const curRowRect = currentRowEl.getBoundingClientRect();
                    const curElRect = current.getBoundingClientRect();
                    const curCenterY = curElRect.top + curElRect.height / 2;

                    const candidates = rows.filter(r => {
                        if (r.rowEl === currentRowEl) return false;
                        return dir === "right"
                            ? r.left >= curRowRect.right - 24
                            : r.right <= curRowRect.left + 24;
                    });
                    if (!candidates.length) return null;

                    // Pick the candidate row whose vertical span is closest to current element's center Y
                    candidates.sort((a, b) => {
                        const aRect = a.rowEl.getBoundingClientRect();
                        const bRect = b.rowEl.getBoundingClientRect();
                        const aDist =
                            curCenterY >= aRect.top && curCenterY <= aRect.bottom
                                ? 0
                                : Math.min(Math.abs(curCenterY - aRect.top), Math.abs(curCenterY - aRect.bottom));
                        const bDist =
                            curCenterY >= bRect.top && curCenterY <= bRect.bottom
                                ? 0
                                : Math.min(Math.abs(curCenterY - bRect.top), Math.abs(curCenterY - bRect.bottom));
                        return aDist - bDist;
                    });
                    return candidates[0];
                };

                if (!isGrid) {
                    // Single horizontal row (e.g. Navbar, Hero actions, Media Row carousel):
                    // Strict index-based Left/Right so rapid key repeats never skip or fail
                    const idx = rowPrimaryItems.indexOf(effectiveCurrent);
                    if (direction === "right") {
                        if (idx !== -1 && idx + 1 < rowPrimaryItems.length) {
                            focusTarget(rowPrimaryItems[idx + 1], currentRowEl);
                            return;
                        }
                        const rightRow = findSideBySideRow("right");
                        if (rightRow) {
                            const remembered = lastRowItemMap.current.get(rightRow.rowId);
                            const target =
                                remembered && rightRow.items.includes(remembered)
                                    ? remembered
                                    : getPrimaryFocusableInRow(rightRow.rowEl)[0];
                            if (target) {
                                focusTarget(target, rightRow.rowEl);
                                return;
                            }
                        }
                        return;
                    }

                    if (direction === "left") {
                        if (idx > 0) {
                            focusTarget(rowPrimaryItems[idx - 1], currentRowEl);
                            return;
                        }
                        const leftRow = findSideBySideRow("left");
                        if (leftRow) {
                            const remembered = lastRowItemMap.current.get(leftRow.rowId);
                            const target =
                                remembered && leftRow.items.includes(remembered)
                                    ? remembered
                                    : getPrimaryFocusableInRow(leftRow.rowEl)[0];
                            if (target) {
                                focusTarget(target, leftRow.rowEl);
                                return;
                            }
                        }
                        return;
                    }
                } else {
                    // 2D Grid inside [data-tv-row] (Keyboard, Search results grid, Episodes grid, Channels grid)
                    const subRows = groupIntoVisualSubRows(rowPrimaryItems, mainScroll);
                    let subRowIdx = -1;
                    let colIdx = -1;
                    for (let r = 0; r < subRows.length; r++) {
                        const c = subRows[r].indexOf(effectiveCurrent);
                        if (c !== -1) {
                            subRowIdx = r;
                            colIdx = c;
                            break;
                        }
                    }

                    if (subRowIdx !== -1) {
                        const currentSubRow = subRows[subRowIdx];
                        if (direction === "right") {
                            if (colIdx + 1 < currentSubRow.length) {
                                focusTarget(currentSubRow[colIdx + 1], currentRowEl);
                                return;
                            }
                            const rightRow = findSideBySideRow("right");
                            if (rightRow) {
                                const remembered = lastRowItemMap.current.get(rightRow.rowId);
                                const target =
                                    remembered && rightRow.items.includes(remembered)
                                        ? remembered
                                        : getPrimaryFocusableInRow(rightRow.rowEl)[0];
                                if (target) {
                                    focusTarget(target, rightRow.rowEl);
                                    return;
                                }
                            }
                            return;
                        }

                        if (direction === "left") {
                            if (colIdx - 1 >= 0) {
                                focusTarget(currentSubRow[colIdx - 1], currentRowEl);
                                return;
                            }
                            const leftRow = findSideBySideRow("left");
                            if (leftRow) {
                                const remembered = lastRowItemMap.current.get(leftRow.rowId);
                                const pathPrimary = getPrimaryFocusableInRow(leftRow.rowEl);
                                const target =
                                    remembered && leftRow.items.includes(remembered)
                                        ? remembered
                                        : pathPrimary[pathPrimary.length - 1] || pathPrimary[0];
                                if (target) {
                                    focusTarget(target, leftRow.rowEl);
                                    return;
                                }
                            }
                            return;
                        }

                        if (direction === "down" && subRowIdx + 1 < subRows.length) {
                            const nextSubRow = subRows[subRowIdx + 1];
                            const targetCol = Math.min(colIdx, nextSubRow.length - 1);
                            focusTarget(nextSubRow[targetCol], currentRowEl);
                            return;
                        }

                        if (direction === "up" && subRowIdx - 1 >= 0) {
                            const prevSubRow = subRows[subRowIdx - 1];
                            const targetCol = Math.min(colIdx, prevSubRow.length - 1);
                            focusTarget(prevSubRow[targetCol], currentRowEl);
                            return;
                        }
                    }
                }

                // 3. Row-to-Row vertical transitions (Up / Down between [data-tv-row] sections)
                if (direction === "down" || direction === "up") {
                    const rows = getVisibleRows(scope, mainScroll);
                    const curRowInfo = rows.find(r => r.rowEl === currentRowEl);

                    if (curRowInfo) {
                        // Filter rows that horizontally overlap with currentRow (or full-width navbar)
                        const overlappingRows = rows.filter(r => {
                            if (r.rowEl === currentRowEl) return true;
                            const overlap = Math.min(curRowInfo.right, r.right) - Math.max(curRowInfo.left, r.left);
                            return overlap > 40 || r.rowId === "tv-top-navbar";
                        });

                        const curIdxInOverlapping = overlappingRows.findIndex(r => r.rowEl === currentRowEl);
                        const targetRowInfo =
                            direction === "down"
                                ? overlappingRows[curIdxInOverlapping + 1]
                                : overlappingRows[curIdxInOverlapping - 1];

                        if (targetRowInfo) {
                            const remembered = lastRowItemMap.current.get(targetRowInfo.rowId);
                            const targetPrimary = getPrimaryFocusableInRow(targetRowInfo.rowEl);
                            if (remembered && targetPrimary.includes(remembered)) {
                                focusTarget(remembered, targetRowInfo.rowEl);
                                return;
                            }

                            if (direction === "up" && isGridContainer(targetRowInfo.rowEl)) {
                                const gridSubRows = groupIntoVisualSubRows(targetPrimary, mainScroll);
                                const lastSubRow = gridSubRows[gridSubRows.length - 1];
                                if (lastSubRow?.length) {
                                    focusTarget(lastSubRow[0], targetRowInfo.rowEl);
                                    return;
                                }
                            }

                            if (targetPrimary.length > 0) {
                                focusTarget(targetPrimary[0], targetRowInfo.rowEl);
                                return;
                            }
                        }
                        return;
                    }
                }
            }

            // 4. Fallback for screens without [data-tv-row] (e.g. standard modals or settings pages)
            const subRows = groupIntoVisualSubRows(allElements, mainScroll);
            let rIdx = -1;
            let cIdx = -1;
            for (let r = 0; r < subRows.length; r++) {
                const c = subRows[r].indexOf(current);
                if (c !== -1) {
                    rIdx = r;
                    cIdx = c;
                    break;
                }
            }
            if (rIdx !== -1) {
                if (direction === "right" && cIdx + 1 < subRows[rIdx].length) {
                    focusTarget(subRows[rIdx][cIdx + 1]);
                } else if (direction === "left" && cIdx - 1 >= 0) {
                    focusTarget(subRows[rIdx][cIdx - 1]);
                } else if (direction === "down" && rIdx + 1 < subRows.length) {
                    const nextRow = subRows[rIdx + 1];
                    focusTarget(nextRow[Math.min(cIdx, nextRow.length - 1)]);
                } else if (direction === "up" && rIdx - 1 >= 0) {
                    const prevRow = subRows[rIdx - 1];
                    focusTarget(prevRow[Math.min(cIdx, prevRow.length - 1)]);
                }
            }
        };

        const handleBackAction = () => {
            if (onBackOverride && onBackOverride()) {
                return;
            }
            const closeBtn = document.querySelector<HTMLElement>(
                ".tv-modal-overlay [data-tv-modal-close='true'], .modal.show .btn-close"
            );
            if (closeBtn && isElementVisible(closeBtn)) {
                closeBtn.click();
                return;
            }
            if (
                location.pathname !== "/squadflix" &&
                location.pathname !== "/squadflix/" &&
                location.pathname !== "/squadflix/home"
            ) {
                navigate(-1);
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            const active = document.activeElement as HTMLElement | null;
            const isTextInput =
                active &&
                (active.tagName === "INPUT" || active.tagName === "TEXTAREA") &&
                (active as HTMLInputElement).type !== "checkbox" &&
                (active as HTMLInputElement).type !== "button";

            if (e.key === "ArrowUp") {
                e.preventDefault();
                moveFocus("up");
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                moveFocus("down");
            } else if (e.key === "ArrowLeft") {
                if (isTextInput) {
                    const input = active as HTMLInputElement;
                    if ((input.selectionStart ?? 0) > 0) return;
                }
                e.preventDefault();
                moveFocus("left");
            } else if (e.key === "ArrowRight") {
                if (isTextInput) {
                    const input = active as HTMLInputElement;
                    if ((input.selectionEnd ?? 0) < (input.value?.length ?? 0)) return;
                }
                e.preventDefault();
                moveFocus("right");
            } else if (e.key === "Enter") {
                if (active && active !== document.body && !isTextInput) {
                    if (active.tagName !== "BUTTON" && active.tagName !== "A" && active.tagName !== "INPUT") {
                        e.preventDefault();
                        active.click();
                    }
                }
            } else if (
                e.key === "Escape" ||
                e.keyCode === 461 || // LG WebOS Back
                e.keyCode === 10009 || // Samsung Tizen Back
                (e.key === "Backspace" && !isTextInput)
            ) {
                e.preventDefault();
                handleBackAction();
            }
        };

        window.addEventListener("keydown", onKeyDown);

        // Optional Gamepad polling support for TV controllers
        let rafId: number | null = null;
        let lastGamepadAction = 0;

        const pollGamepad = () => {
            const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
            const gp = gamepads[0];
            if (gp) {
                const now = performance.now();
                if (now - lastGamepadAction > 190) {
                    const axeX = gp.axes[0] || 0;
                    const axeY = gp.axes[1] || 0;
                    if (gp.buttons[12]?.pressed || axeY < -0.55) {
                        moveFocus("up");
                        lastGamepadAction = now;
                    } else if (gp.buttons[13]?.pressed || axeY > 0.55) {
                        moveFocus("down");
                        lastGamepadAction = now;
                    } else if (gp.buttons[14]?.pressed || axeX < -0.55) {
                        moveFocus("left");
                        lastGamepadAction = now;
                    } else if (gp.buttons[15]?.pressed || axeX > 0.55) {
                        moveFocus("right");
                        lastGamepadAction = now;
                    } else if (gp.buttons[0]?.pressed) {
                        (document.activeElement as HTMLElement)?.click?.();
                        lastGamepadAction = now + 120;
                    } else if (gp.buttons[1]?.pressed) {
                        handleBackAction();
                        lastGamepadAction = now + 150;
                    }
                }
            }
            rafId = requestAnimationFrame(pollGamepad);
        };

        rafId = requestAnimationFrame(pollGamepad);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [enabled, location.pathname, navigate, onBackOverride]);
};

export default useTvSpatialNavigation;
